import { Fragment, useState } from "react";
import { createGlobalStyle } from "styled-components";
import useForm from "antd/es/form/hooks/useForm";
import { message } from "antd";
import { getTranslateNsT } from "@/utils/getTranslateNsT";
import { Button, Form, Input } from "@renderbus/ui";
import { Modal } from "@/components/renderbus-modal";
import { useSubAccountStore } from "@/store/SubAccountStore";
import { useGlobalStore } from "@/store/GlobalStore";
import request from "project-config/services/request/request";
import { TranferType } from "@/pages/child-customer/view/child-manage/components/child-operation/constants/transfer";
import { ISubAccountData } from "@/store/SubAccountStore";
import { TransferTip } from "@/pages/child-customer/view/child-manage/atom";
import { TextButton } from "@/pages/child-customer/atom";
import { isEn } from "project-config/utils/env";

import { ToolTip } from "task-eva-ui";

export interface ITransferProps {
  transferMoney: string;
}

export function TransferButton(
  row: ISubAccountData & {
    type: TranferType;
    isDingTalkSubAccount?: boolean;
  }
) {
  const {
    id,
    status,
    type,
    subUserName,
    rmbBalance,
    usdBalance,
    isDingTalkSubAccount,
    deptId,
  } = row;
  const [isOpen, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [form] = useForm<ITransferProps>();
  const t = getTranslateNsT("center");
  const { userInfo, isDomestic, currencySymbol, fetchUserData } =
    useGlobalStore();
  const { fetchSubAccountList, fetchDingTalkSubAccountList, isSearchSubUser } =
    useSubAccountStore();

  const isTransferIn = type === TranferType.TRANSFER_IN;
  const title = isTransferIn
    ? t("user_com_childManage_del_render_btn_shiftTo")
    : t("user_com_childManage_del_render_btn_shiftOut");
  const availableMoneyTip = isTransferIn
    ? t("user_com_shiftto_label_enable_fund")
    : t("user_com_shiftout_label_enable_fund");
  const transferMoneyTip = isTransferIn
    ? t("user_com_shiftto_label_fund")
    : t("user_com_shiftout_label_fund");
  const availableShiftToMoney = isDomestic
    ? userInfo?.rmbbalance
    : userInfo?.usdbalance;
  const availableShiftOutMoney = isDomestic ? rmbBalance : usdBalance;
  const availableMoney = isTransferIn
    ? availableShiftToMoney
    : availableShiftOutMoney;
  const errorTip = isTransferIn
    ? t("user_com_shiftto")
    : t("user_com_shiftout");
  const availableMoneyLabel = `${currencySymbol}${availableMoney}`;
  const transferMoneyValidator = (rule: any, value: number, callback: any) => {
    const [integer, decimal] = String(value).split(".");
    if (decimal && decimal.length > 3) {
      form.setFieldValue(
        "transferMoney",
        +(integer + "." + decimal.slice(0, 3))
      );
    }
    if (!value) callback(new Error("user_com_shiftto_label_fund_empty"));
    if (value > availableMoney! || !/^[0-9.]*$/.test(String(value)))
      callback(new Error("user_com_shiftto_label_fund_illegal"));
    callback();
  };

  function handleCancel() {
    setOpen(false);
    form.resetFields();
  }

  function handleTransfer() {
    if (isLoading) return;
    form.validateFields().then(async () => {
      setIsLoading(true);
      const res = await request.post("/api/rendering/user/transferAccount", {
        id,
        transferMoney: form.getFieldValue("transferMoney"),
        type,
      });
      setIsLoading(false);
      if (res.result) {
        const tip = isTransferIn
          ? t("user_com_shiftto_amount_success")
          : t("user_com_shiftout_amount_success");
        message.success(tip);
        fetchUserData();
        if (isDingTalkSubAccount) {
          fetchDingTalkSubAccountList({
            deptId: deptId,
            subUserId: isSearchSubUser ? id : undefined,
          });
        } else {
          fetchSubAccountList();
        }
      } else {
        const tip = isTransferIn
          ? t("user_com_shiftto_amount_fail")
          : t("user_com_shiftout_amount_fail");
        message.error(tip);
      }
      form.resetFields();
      setOpen(false);
    });
  }

  return (
    <Fragment>
      <ToolTip
        content={t("user_com_childManage_del_render_btn_tip1")}
        trigger="hover"
        hidden={Boolean(status)}
      >
        <TextButton
          disabled={Boolean(!status)}
          type="text"
          onClick={() => setOpen(true)}
        >
          {title}
        </TextButton>
      </ToolTip>
      <Modal
        open={isOpen}
        title={title}
        className={`renderbus-ui--modal transfer ${
          isTransferIn ? "in" : "out"
        }`}
        children={
          <Fragment>
            {isTransferIn && (
              <TransferTip>
                {t("user_com_shiftto_title")}
                <span>{subUserName}</span>
              </TransferTip>
            )}
            {!isTransferIn && (
              <TransferTip>
                {t("user_com_shiftout_title")}
                <span>{subUserName}</span>
                {t("user_com_shiftout_label_outto_admin")}
              </TransferTip>
            )}
            <Form
              size={"large"}
              colon={false}
              requiredMark={false}
              form={form}
              labelCol={{ span: isEn() ? 8 : 6 }}
              labelAlign="right"
            >
              <Form.Item label={availableMoneyTip}>
                {availableMoneyLabel}
              </Form.Item>
              <Form.Item
                key="transferMoney"
                name="transferMoney"
                label={transferMoneyTip}
                rules={[
                  {
                    validator: transferMoneyValidator,
                    message: errorTip,
                  },
                ]}
              >
                <Input
                  className="background-2C2C31"
                  placeholder={t("user_com_shiftto_label_fund_please")}
                />
              </Form.Item>
            </Form>
          </Fragment>
        }
        onCancel={handleCancel}
        onOk={handleTransfer}
        footer={[
          <Button key="back" onClick={handleCancel}>
            {t("submit_cgfile_config_modal_cancel")}
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={isLoading}
            onClick={handleTransfer}
          >
            {t("submit_cgfile_config_modal_comfirm")}
          </Button>,
        ]}
      />
      <TransferStyle />
    </Fragment>
  );
}

const TransferStyle = createGlobalStyle`
  .transfer-input {
    background: #2C2C31 !important;
    width: ${isEn() ? "200px" : "160px"};
  }
  .transfer {
    .ant-modal-body {
      padding: 24px 24px 22px !important;
    }
    &.in .ant-form-item {
      margin-left: ${isEn() ? "-25px" : "-55px"};
    }
    &.out .ant-form-item {
      margin-left: ${isEn() ? "-40px" : "-55px"};
    }
    input {
      width: 200px;
    }
  }
`;
